import React, { useEffect, useState } from 'react';
import { AcademicCapIcon, ClockIcon, InformationCircleIcon, MusicalNoteIcon, UserIcon } from '@heroicons/react/24/outline';
import { clasesAPI, clasesAlumnosAPI } from '../../services/api';
import { formatTime, getDayName } from '../../services/utils';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

const StudentClassesModal = ({ isOpen, onClose, student, currentYear }) => {
    const [studentClasses, setStudentClasses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && student && currentYear) {
            fetchStudentClasses();
        }
    }, [isOpen, student, currentYear]);

    const getClassIdFromAssignment = (assignment) => {
        return assignment?.ClaseID || assignment?.IDClase || assignment?.claseId || assignment?.claseID || assignment?.Clase?.IDClase || null;
    };

    const sortClassesBySchedule = (classes) => {
        return [...classes].sort((a, b) => {
            const dayA = Number(a.DiaSemana || 0);
            const dayB = Number(b.DiaSemana || 0);

            if (dayA !== dayB) {
                return dayA - dayB;
            }

            const startA = a.H_Inicio || '';
            const startB = b.H_Inicio || '';
            return startA.localeCompare(startB);
        });
    };

    const fetchStudentClasses = async () => {
        if (!student) {
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const [assignmentsRes, classesRes] = await Promise.all([
                clasesAlumnosAPI.getByAlumnoId(student.IDAlumno),
                clasesAPI.getByYear(currentYear)
            ]);

            const classIds = new Set(
                assignmentsRes.data
                    .map(getClassIdFromAssignment)
                    .filter(Boolean)
                    .map(id => Number(id))
            );

            const classesForStudent = classesRes.data.filter(clase => classIds.has(Number(clase.IDClase)));

            setStudentClasses(sortClassesBySchedule(classesForStudent));
        } catch (err) {
            setError('Error al cargar las clases del alumno');
            setStudentClasses([]);
            console.error('Error fetching student classes:', err);
        } finally {
            setLoading(false);
        }
    };

    const getStudentFullName = () => {
        if (!student) {
            return '';
        }

        return `${student.Nombre} ${student.Apellido1} ${student.Apellido2 || ''}`.trim();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Información de Clases"
            size="xl"
        >
            <div className="space-y-5">
                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center text-gray-900 font-medium">
                        <InformationCircleIcon className="h-5 w-5 mr-2 text-primary-600" />
                        {getStudentFullName()}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                        Curso {currentYear}/{Number(currentYear) + 1}
                    </p>
                </div>

                {loading && <LoadingSpinner text="Cargando clases del alumno..." />}

                {!loading && error && <ErrorMessage message={error} onRetry={fetchStudentClasses} />}

                {!loading && !error && (
                    <>
                        {studentClasses.length > 0 ? (
                            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                                {studentClasses.map(clase => (
                                    <div key={clase.IDClase} className="border border-gray-200 rounded-lg p-4 bg-white">
                                        <div className="font-semibold text-gray-900 flex items-center mb-3">
                                            <AcademicCapIcon className="h-5 w-5 mr-2 text-gray-600" />
                                            {clase.Asignatura || 'Clase'}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                            <div className="flex items-start text-gray-700">
                                                <UserIcon className="h-4 w-4 mr-2 mt-0.5 text-gray-500" />
                                                <span>
                                                    <strong>Profesor:</strong> {clase.Profesor || 'No especificado'}
                                                </span>
                                            </div>

                                            <div className="flex items-start text-gray-700">
                                                <MusicalNoteIcon className="h-4 w-4 mr-2 mt-0.5 text-gray-500" />
                                                <span>
                                                    <strong>Instrumento:</strong> {clase.Instrumento || 'No especificado'}
                                                </span>
                                            </div>

                                            <div className="flex items-start text-gray-700">
                                                <ClockIcon className="h-4 w-4 mr-2 mt-0.5 text-gray-500" />
                                                <span>
                                                    <strong>Horario:</strong>{' '}
                                                    {getDayName(clase.DiaSemana)} - {formatTime(clase.H_Inicio)} a {formatTime(clase.H_Final)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                                El alumno no tiene clases asignadas en este curso
                            </div>
                        )}
                    </>
                )}

                <div className="pt-4 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default StudentClassesModal;